import { Dialog } from "primereact/dialog"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import "./ProductRatingModal.css"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons"
import { Tooltip } from "primereact/tooltip"

interface Props {
    visible: boolean
    onHide: () => void
    data: any
    loading?: boolean
    model?: any
}

const ProductRatingModal = ({ visible, onHide, data, loading, model }: Props) => {

    const footer = (
        <div className="w-full">
            <div className="w-full border-t border-gray-300 mb-4"></div>
            <div className="flex justify-center">
                <button
                    onClick={onHide}
                    className="ul-button -app-tertiary -medium hover:!bg-gray-50 hover:!text-black"
                >
                    CLOSE
                </button>
            </div>
        </div>
    )

    const header = (col: any) => (
        <div style={{ fontSize: ".875rem", fontWeight: "600" }} className="flex items-center justify-center gap-1 flex-wrap text-center leading-tight">
            <span className="font-medium">{col.displayName}</span>

            {col.uom && col.uom !== "N/A" && (
                <span className="italic">
                    ({col.uom})
                </span>
            )}

            {col.helperText && (
                <>
                    <FontAwesomeIcon
                        icon={faCircleInfo}
                        className={`info-icon-${col.key} ml-1`}
                        data-pr-tooltip={col.helperText}
                    />

                    <Tooltip
                        target={`.info-icon-${col.key}`}
                        position="top"
                        appendTo={document.body}
                        className="no-arrow-tooltip"
                    />
                </>
            )}
        </div>
    )


    const getUom = (item: any) => (
        item.uom && item.uom !== "N/A" && (
            <span className="italic">
                ({item.uom})
            </span>
        )
    );


    const getOrderedContent = (section: any) => {
        const items = section.items || []
        const tables = section.tables || []

        const combined = [
            ...items.map((i: any) => ({ type: "item", ...i })),
            ...tables.map((t: any) => ({ type: "table", ...t }))
        ]

        return combined.sort((a: any, b: any) => a.sequence - b.sequence)
    }


    const renderEllipsisCell = (
        value: any,
        rowIndex: number,
        colKey: string,
        dataType?: string
    ) => {
        const hasValue = value && value !== "--";
        const id = `tooltip-${rowIndex}-${colKey}`;
        const isValueNoContent = value === "No Content";

        const isContent = dataType === "content";

        if (isContent && !hasValue) {
            return <div>No Content</div>
        }

        return (
            <>
                {hasValue && !isValueNoContent && !isContent && (
                    <Tooltip
                        position="top"
                        target={`#${id}`}
                        content={value}
                        className="no-arrow-tooltip"
                    />
                )}

                <div
                    id={id}
                    style={
                        isContent
                            ? {
                                whiteSpace: "normal",
                                wordBreak: "keep-all",
                                overflowWrap: "break-word",
                            }
                            : {
                                display: "inline-block",
                                maxWidth: "100%",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                verticalAlign: "top",

                            }
                    }
                >
                    {hasValue ? value : "--"}
                </div>
            </>
        );
    };

    const renderEllipsisWithTooltip = (
        value: any,
        id: string
    ) => {
        const hasValue = value && value !== "--";

        return (
            <>
                <div
                    id={id}
                    className={"ellipsis-text"}
                >
                    {hasValue ? value : "--"}
                </div>

                {hasValue && (
                    <Tooltip
                        target={`#${id}`}
                        content={value}
                        position="top"
                        className="no-arrow-tooltip"
                    />
                )}
            </>
        );
    };


    return (
        <>
            <Dialog
                header="Product Ratings"
                visible={visible}
                onHide={onHide}
                modal
                style={{ width: "100vw", maxHeight: "100vh", margin: 0 }}
                className="custom-dialog"
                draggable={false}
                resizable={false}
                footer={footer}
            >

                <div className="h-full flex flex-col overflow-y-auto mt-4">
                    {loading && (
                        <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
                            <span className="mb-3">Fetching attributes...</span>

                            <svg className="ul-progressCircle -medium">
                                <circle className="ul-progressCircle__outer" cx="50%" cy="50%" r="35%" />
                                <circle className="ul-progressCircle__inner" cx="50%" cy="50%" r="35%" />
                            </svg>
                        </div>
                    )}

                    {!loading && (
                        <>
                            <div className="mb-4">
                                <div className="flex gap-6 text-sm">

                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold">Model Name</div>
                                        {renderEllipsisWithTooltip(data?.asset?.modelName ?? model.modelName, "model-tooltip")}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold">Product Type</div>
                                        {renderEllipsisWithTooltip(data?.asset?.productType ?? model?.productType, "productType-tooltip")}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold">Status</div>
                                        {renderEllipsisWithTooltip(data?.asset?.status ?? model?.status, "status-tooltip")}
                                    </div>

                                </div>
                            </div>

                            {data?.sections?.map((section: any, idx: number) => {
                                const ordered = getOrderedContent(section)

                                return (

                                    <div key={idx} className="space-y-4">

                                        <h2 className="text-lg font-semibold pb-2">
                                            {section.label}
                                        </h2>

                                        <div className="flex flex-wrap gap-6">

                                            {ordered.map((item: any, i: number) => {

                                                if (item.type === "item") {
                                                    const valueId = `value-tooltip-${item.dataParamName}-${i}`;

                                                    return (
                                                        <div
                                                            key={i}
                                                            className="basis-[calc(33.333%-1rem)] min-w-0"
                                                        >
                                                            <div className="font-semibold text-sm">
                                                                <span>{item.displayName} {getUom(item)}</span>

                                                                {item.helpText && (
                                                                    <>
                                                                        <FontAwesomeIcon
                                                                            icon={faCircleInfo}
                                                                            className={`info-icon-${item.dataParamName} ml-2`}
                                                                            data-pr-tooltip={item.helpText}
                                                                        />
                                                                        <Tooltip
                                                                            target={`.info-icon-${item.dataParamName}`}
                                                                            position="top"
                                                                            className="no-arrow-tooltip" />


                                                                    </>
                                                                )}
                                                            </div>
                                                            <div className="text-sm">
                                                                {renderEllipsisWithTooltip(item.value, valueId)}
                                                            </div>

                                                        </div>
                                                    )
                                                }

                                                if (item.type === "table") {

                                                    const tableData =
                                                        !item.rows || item.rows.length === 0
                                                            ? [
                                                                Object.fromEntries(
                                                                    item.columns.map((col: any) => [col.key, "--"])
                                                                ),
                                                            ]
                                                            : item.rows;

                                                    return (
                                                        <div key={i} className="w-full">
                                                            <DataTable
                                                                value={tableData}
                                                                style={{ width: "100%" }}
                                                                showGridlines
                                                                stripedRows
                                                                emptyMessage="No data available"
                                                                tableStyle={{ tableLayout: "fixed", width: "100%" }}
                                                                className="custom-rating-table"
                                                            >
                                                                {item.columns.map((col: any, colIdx: number) => (
                                                                    <Column
                                                                        key={colIdx}
                                                                        field={col.key}
                                                                        header={header(col)}
                                                                        headerStyle={{ textAlign: "center" }}
                                                                        body={(rowData, options) =>
                                                                            renderEllipsisCell(
                                                                                rowData[col.key],
                                                                                options.rowIndex,
                                                                                col.key,
                                                                                col.dataType
                                                                            )
                                                                        }
                                                                        style={{
                                                                            width: `${100 / item.columns.length}%`,
                                                                            whiteSpace: "normal",
                                                                            wordBreak: "break-word",
                                                                            textAlign: col.dataType === "content" ? "left" : "center",
                                                                        }}
                                                                    />
                                                                ))}
                                                            </DataTable>
                                                        </div>
                                                    )
                                                }
                                                return null
                                            })}
                                        </div>
                                    </div>

                                )
                            })}
                        </>
                    )}
                </div>
            </Dialog>
        </>
    )
}

export default ProductRatingModal